function formatMoney(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount / 100);
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Returns true if all remaining items in the cart are upsells (no "real" products)
function cartOnlyContainsUpsells(cart, upsellIds) {
  if (!cart.items || cart.items.length === 0) return false;
  return cart.items.every((i) => upsellIds.includes(i.variant_id));
}

// Global flag to suppress Dawn's automatic refresh when our upsell is working
window.__cartUpsellWorking = false;
// Session memory of manually deselected upsells (resets when cart is fully emptied)
window.__manuallyDeselectedUpsells = new Set();

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isCartUpdating = false;

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.cartDrawer = this.querySelector(".drawer__inner");
    this.cartDrawer.addEventListener("click", this.handleCartDrawerClick.bind(this));
    this.updateCartDebounced = debounce(this.updateCart.bind(this), 500);
    this.initializeUpsellButtons();
    this.initializeVariantSelectors();
    this.setupUpsellReinitObserver();
    this.autoAddPreselectedUpsells();
    this.watchForOrphanUpsells();
  }

  setupUpsellReinitObserver() {
    const observer = new MutationObserver(() => {
      this.initializeUpsellButtons();
      this.initializeVariantSelectors();
      this.clearCartErrors();
    });
    observer.observe(this, { childList: true, subtree: true });
  }

  clearCartErrors() {
    const errorContainer = document.querySelector('#CartDrawer-CartErrors');
    if (errorContainer) errorContainer.innerHTML = '';
    document.querySelectorAll('.cart-item__error-text').forEach(el => el.textContent = '');
  }

  // Watch for cart events → if only upsells remain (no real product), empty the cart
  watchForOrphanUpsells() {
    const check = () => {
      const upsellIds = window.CART_DRAWER_UPSELL_IDS || [];
      if (upsellIds.length === 0) return;
      if (this.isCartUpdating) return;

      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          if (cartOnlyContainsUpsells(cart, upsellIds)) {
            this.emptyOrphanUpsells();
          }
        })
        .catch(() => {});
    };

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(check, 800);
      });
    }

    document.addEventListener('click', (e) => {
      if (e.target.closest('.cart-remove-button') || e.target.closest('.quantity__button')) {
        setTimeout(check, 1200);
      }
    });
  }

  emptyOrphanUpsells() {
    if (this.isCartUpdating) return;
    this.isCartUpdating = true;
    window.__cartUpsellWorking = true;

    // Reset manual deselect memory so next time upsell auto-adds again
    window.__manuallyDeselectedUpsells.clear();

    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(() => this.refreshCartDrawer())
      .catch((err) => console.error('Error clearing orphan upsells:', err))
      .finally(() => {
        setTimeout(() => {
          this.isCartUpdating = false;
          window.__cartUpsellWorking = false;
          this.clearCartErrors();
        }, 700);
      });
  }

  // Auto-adds ALL upsells marked as preselected, unless user manually deselected them THIS session
  autoAddPreselectedUpsells() {
    const checkboxes = this.querySelectorAll('.upsell-checkbox[data-preselected="true"]');
    if (checkboxes.length === 0) return;

    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        const upsellIds = window.CART_DRAWER_UPSELL_IDS || [];

        // Only add upsells if cart has at least one non-upsell (real) product
        const nonUpsellItems = cart.items.filter((i) => !upsellIds.includes(i.variant_id));
        if (nonUpsellItems.length === 0) return;

        const cartVariantIds = cart.items.map((i) => i.variant_id);
        const toAdd = [];

        checkboxes.forEach((cb) => {
          const vid = parseInt(cb.getAttribute('data-product-id'), 10);
          const alreadyInCart = cartVariantIds.includes(vid);
          const userDeselected = window.__manuallyDeselectedUpsells.has(vid);

          // Add only if: not already in cart AND user hasn't manually turned it off
          if (!alreadyInCart && !userDeselected) {
            toAdd.push({ id: vid, quantity: 1 });
          }
        });

        if (toAdd.length > 0) {
          this.isCartUpdating = true;
          window.__cartUpsellWorking = true;
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: toAdd }),
          })
            .then(() => this.refreshCartDrawer())
            .catch((err) => console.error('Error auto-adding upsell:', err))
            .finally(() => {
              setTimeout(() => {
                this.isCartUpdating = false;
                window.__cartUpsellWorking = false;
                this.clearCartErrors();
              }, 700);
            });
        }
      })
      .catch(() => {});
  }

  initializeUpsellButtons() {
    var upsellAddButtons = this.querySelectorAll(".upsell-add-button");
    upsellAddButtons.forEach((button) => {
      if (button.dataset.upsellInit) return;
      button.dataset.upsellInit = "1";
      button.addEventListener("click", this.handleUpsellButtonClick.bind(this));
    });

    var upsellCheckboxes = this.querySelectorAll(".upsell-checkbox");
    upsellCheckboxes.forEach((checkbox) => {
      if (checkbox.dataset.upsellInit) return;
      checkbox.dataset.upsellInit = "1";
      checkbox.addEventListener("change", this.handleUpsellChange.bind(this));
    });

    this.syncUpsellTogglesWithCart();
  }

  syncUpsellTogglesWithCart() {
    var checkboxes = this.querySelectorAll(".upsell-checkbox");
    if (checkboxes.length === 0) return;

    fetch("/cart.js")
      .then((r) => r.json())
      .then((cart) => {
        checkboxes.forEach((cb) => {
          var vid = parseInt(cb.getAttribute("data-product-id"), 10);
          cb.checked = cart.items.some((i) => i.variant_id === vid);
        });
      })
      .catch(() => {});
  }

  initializeVariantSelectors() {
    const variantSelectors = this.querySelectorAll('.upsell-product__variant-selector');
    variantSelectors.forEach((selector) => {
      if (selector.dataset.upsellInit) return;
      selector.dataset.upsellInit = "1";
      selector.addEventListener('change', this.handleVariantChange.bind(this));
    });
  }

  handleVariantChange(event) {
    const selector = event.target;
    const selectedOption = selector.options[selector.selectedIndex];
    const productContainer = selector.closest('.upsell-product');
    const productImage = productContainer.querySelector('.upsell-product-image');
    const imageUrl = selectedOption.getAttribute('data-image-url');
    const defaultImage = productImage.getAttribute('data-default-image');

    if (imageUrl && !imageUrl.includes('no-image')) {
      productImage.src = imageUrl;
    } else {
      productImage.src = defaultImage;
    }

    const addButton = productContainer.querySelector('.upsell-add-button');
    const checkbox = productContainer.querySelector('.upsell-checkbox');
    if (addButton) addButton.setAttribute('data-product-id', selector.value);
    if (checkbox) {
      checkbox.setAttribute('data-product-id', selector.value);
      checkbox.id = `upsell-product-${selector.value}`;
    }
  }

  handleUpsellButtonClick(event) {
    event.stopPropagation();
    var button = event.target.closest(".upsell-add-button");
    var productId = button.getAttribute("data-product-id");
    var variantSelector = button.closest(".upsell-product").querySelector(".upsell-product__variant-selector");
    var variantId = variantSelector ? variantSelector.value : null;
    if (!productId) return;
    this.addProductToCart(productId, variantId, true);
  }

  handleCartDrawerClick(event) {
    const quantityButton = event.target.closest(".quantity__button");
    const removeButton = event.target.closest(".cart-remove-button");
    if (quantityButton || removeButton) {
      setTimeout(() => {
        this.initializeUpsellButtons();
        this.initializeVariantSelectors();
      }, 1000);
    }
  }

  handleUpsellChange(event) {
    var checkbox = event.target;
    var productId = checkbox.getAttribute("data-product-id");
    var variantSelector = checkbox.closest(".upsell-product").querySelector(".upsell-product__variant-selector");
    var variantId = variantSelector ? variantSelector.value : null;
    if (!productId) return;

    const vid = parseInt(productId, 10);

    if (checkbox.checked) {
      // User re-activated it → remove from "deselected" list
      window.__manuallyDeselectedUpsells.delete(vid);
      this.addProductToCart(productId, variantId, true);
    } else {
      // User manually turned it off → remember for this session
      window.__manuallyDeselectedUpsells.add(vid);
      this.removeProductFromCart(productId);
    }
  }

  // Rebuilds the entire drawer inner so the empty state is rendered correctly
  refreshCartDrawer() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Replace the entire drawer__inner to ensure empty state renders correctly
        const targetInner = document.querySelector('.drawer__inner');
        const sourceInner = html.querySelector('.drawer__inner');
        if (targetInner && sourceInner) {
          const targetClasses = targetInner.className;
          targetInner.innerHTML = sourceInner.innerHTML;
          targetInner.className = targetClasses;
        } else {
          // Fallback to granular replacement
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        }

        // Update the custom header (item count)
        const targetHeader = document.querySelector('.custom-header h2');
        const sourceHeader = html.querySelector('.custom-header h2');
        if (targetHeader && sourceHeader) {
          targetHeader.innerHTML = sourceHeader.innerHTML;
        }

        this.clearCartErrors();
        return fetch('/cart.js');
      })
      .then((r) => r.json())
      .then((cart) => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          if (cart.item_count === 0) {
            drawer.classList.add('is-empty');
          } else {
            drawer.classList.remove('is-empty');
          }
        }
        return fetch(`${routes.cart_url}?section_id=cart-icon-bubble`);
      })
      .then((r) => r.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newBubble = parsed.querySelector('.shopify-section');
        const currentBubble = document.querySelector('#cart-icon-bubble .shopify-section')
          || document.querySelector('#cart-icon-bubble');
        if (currentBubble && newBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      })
      .catch((err) => console.error('Error refreshing cart:', err));
  }

  updateCart() {
    this.refreshCartDrawer();
  }

  addProductToCart(productId, variantId, isUpsell = false) {
    if (!productId) return;
    if (this.isCartUpdating) return;
    this.isCartUpdating = true;
    window.__cartUpsellWorking = true;

    const itemData = {
      items: [{ id: variantId ? variantId : productId, quantity: 1 }]
    };

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(itemData),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to add: " + response.statusText);
        return response.json();
      })
      .then(() => {
        if (isUpsell) return this.refreshCartDrawer();
      })
      .catch((error) => console.error("Error adding product:", error))
      .finally(() => {
        setTimeout(() => {
          this.isCartUpdating = false;
          window.__cartUpsellWorking = false;
          this.clearCartErrors();
        }, 700);
      });
  }

  removeProductFromCart(variantId) {
    if (!variantId) return;
    if (this.isCartUpdating) return;
    this.isCartUpdating = true;
    window.__cartUpsellWorking = true;

    fetch("/cart.js")
      .then((r) => r.json())
      .then((cart) => {
        var item = cart.items.find((i) => i.variant_id === parseInt(variantId, 10));
        if (!item) {
          this.isCartUpdating = false;
          window.__cartUpsellWorking = false;
          return;
        }
        return fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      })
      .then(() => this.refreshCartDrawer())
      .catch((err) => console.error("Error removing product:", err))
      .finally(() => {
        setTimeout(() => {
          this.isCartUpdating = false;
          window.__cartUpsellWorking = false;
          this.clearCartErrors();
        }, 700);
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);

    this.autoAddPreselectedUpsells();

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.querySelector("#CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
        this.initializeUpsellButtons();
        this.initializeVariantSelectors();
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");
    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });
    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });

    this.dispatchEvent(new CustomEvent("cartUpdated", { bubbles: true }));
    this.initializeUpsellButtons();
    this.initializeVariantSelectors();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-drawer", selector: ".cart-drawer__footer" },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
    ];
  }

  onCartUpdate() {
    if (window.__cartUpsellWorking) return;
    super.onCartUpdate();
  }

  updateQuantity(line, quantity, name, variantId) {
    if (window.__cartUpsellWorking) return;
    if (super.updateQuantity) {
      super.updateQuantity(line, quantity, name, variantId);
    }
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);